import { __rest, __decorate } from 'tslib';
import { ReactWrapperComponent, AngularReact, registerElement } from '@angular-react/core';
import * as i0 from '@angular/core';
import { Directive, Input, ContentChildren, EventEmitter, Component, ChangeDetectionStrategy, ViewChild, ContentChild, Output, NgModule, NO_ERRORS_SCHEMA } from '@angular/core';
import { Styled } from '@angular-react/fabric/lib/utils';
import { CommonModule } from '@angular/common';
import { Dropdown } from '@fluentui/react/lib/Dropdown';

// Copyright (c) Microsoft Corporation. All rights reserved.
/**
 * Wrapper directive for creating a DropdownOption
 * @paramName optionKey Binds to React 'key' property.
 *                      Name change necessary because key is a reserved attribute in the wrapper component.
 */
class DropdownOptionDirective {
}
/** @nocollapse */ DropdownOptionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DropdownOptionDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ DropdownOptionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: DropdownOptionDirective, selector: "fab-dropdown-option", inputs: { optionKey: "optionKey", text: "text", title: "title", itemType: "itemType", index: "index", ariaLabel: "ariaLabel", selected: "selected", disabled: "disabled", hidden: "hidden", data: "data" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DropdownOptionDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-dropdown-option' }]
        }], propDecorators: { optionKey: [{
                type: Input
            }], text: [{
                type: Input
            }], title: [{
                type: Input
            }], itemType: [{
                type: Input
            }], index: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], selected: [{
                type: Input
            }], disabled: [{
                type: Input
            }], hidden: [{
                type: Input
            }], data: [{
                type: Input
            }] } });

/**
 * Wrapper directive for creating multiple DropdownOptions
 * Note that if you use this, it will override the imperative [options] binding.
 */
class DropdownOptionsDirective {
    get items() {
        return this.directiveItems.map((_a) => {
            var { optionKey } = _a, otherDirectiveProps = __rest(_a, ["optionKey"]);
            return (Object.assign({ key: optionKey }, otherDirectiveProps));
        });
    }
}
/** @nocollapse */ DropdownOptionsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DropdownOptionsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
/** @nocollapse */ DropdownOptionsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.3.1", type: DropdownOptionsDirective, selector: "fab-dropdown > options", queries: [{ propertyName: "directiveItems", predicate: DropdownOptionDirective }], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: DropdownOptionsDirective, decorators: [{
            type: Directive,
            args: [{ selector: 'fab-dropdown > options' }]
        }], propDecorators: { directiveItems: [{
                type: ContentChildren,
                args: [DropdownOptionDirective]
            }] } });

let FabDropdownComponent = class FabDropdownComponent extends ReactWrapperComponent {
    constructor(elementRef, changeDetectorRef, renderer) {
        super(elementRef, changeDetectorRef, renderer, { setHostDisplay: true });
        this.onChange = new EventEmitter();
        this.onDismiss = new EventEmitter();
        this.onChangeHandler = this.onChangeHandler.bind(this);
        this.onDismissHandler = this.onDismissHandler.bind(this);
    }
    ngOnInit() {
        this.onRenderContainer = this.createRenderPropHandler(this.renderContainer);
        this.onRenderList = this.createRenderPropHandler(this.renderList);
        this.onRenderItem = this.createRenderPropHandler(this.renderItem);
        this.onRenderOption = this.createRenderPropHandler(this.renderOption);
        this.onRenderPlaceholder = this.createRenderPropHandler(this.renderPlaceholder);
        this.onRenderPlaceHolder = this.createRenderPropHandler(this.renderPlaceHolder);
        this.onRenderTitle = this.createRenderPropHandler(this.renderTitle);
        this.onRenderCaretDown = this.createRenderPropHandler(this.renderCaretDown);
    }
    ngAfterContentInit() {
        if (this.dropdownOptionsDirective) {
            this._initDirective(this.dropdownOptionsDirective);
        }
        super.ngAfterContentInit();
    }
    onChangeHandler(event, option, index) {
        this.onChange.emit({
            event: event && event.nativeEvent,
            option,
            index,
        });
    }
    onDismissHandler() {
        this.onDismiss.emit();
    }
    _initDirective(directive) {
        this.options = directive.items;
        this.markForCheck();
    }
};
/** @nocollapse */ FabDropdownComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDropdownComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }, { token: i0.Renderer2 }], target: i0.ɵɵFactoryTarget.Component });
/** @nocollapse */ FabDropdownComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.3.1", type: FabDropdownComponent, selector: "fab-dropdown", inputs: { componentRef: "componentRef", label: "label", ariaLabel: "ariaLabel", id: "id", className: "className", defaultSelectedKey: "defaultSelectedKey", selectedKey: "selectedKey", disabled: "disabled", required: "required", calloutProps: "calloutProps", panelProps: "panelProps", errorMessage: "errorMessage", placeholder: "placeholder", openOnKeyboardFocus: "openOnKeyboardFocus", options: "options", dropdownWidth: "dropdownWidth", responsiveMode: "responsiveMode", multiSelect: "multiSelect", defaultSelectedKeys: "defaultSelectedKeys", selectedKeys: "selectedKeys", multiSelectDelimiter: "multiSelectDelimiter", notifyOnReselect: "notifyOnReselect", theme: "theme", styles: "styles", renderContainer: "renderContainer", renderList: "renderList", renderItem: "renderItem", renderOption: "renderOption", renderPlaceholder: "renderPlaceholder", renderPlaceHolder: "renderPlaceHolder", renderTitle: "renderTitle", renderCaretDown: "renderCaretDown" }, outputs: { onChange: "onChange", onDismiss: "onDismiss" }, queries: [{ propertyName: "dropdownOptionsDirective", first: true, predicate: DropdownOptionsDirective, descendants: true, static: true }], viewQueries: [{ propertyName: "reactNodeRef", first: true, predicate: ["reactNode"], descendants: true, static: true }], exportAs: ["fabDropdown"], usesInheritance: true, ngImport: i0, template: `
    <Dropdown
      #reactNode
      [componentRef]="componentRef"
      [label]="label"
      [ariaLabel]="ariaLabel"
      [id]="id"
      [className]="className"
      [defaultSelectedKey]="defaultSelectedKey"
      [selectedKey]="selectedKey"
      [disabled]="disabled"
      [required]="required"
      [calloutProps]="calloutProps"
      [panelProps]="panelProps"
      [errorMessage]="errorMessage"
      [placeholder]="placeholder"
      [openOnKeyboardFocus]="openOnKeyboardFocus"
      [options]="options"
      [dropdownWidth]="dropdownWidth"
      [responsiveMode]="responsiveMode"
      [multiSelect]="multiSelect"
      [defaultSelectedKeys]="defaultSelectedKeys"
      [selectedKeys]="selectedKeys"
      [multiSelectDelimiter]="multiSelectDelimiter"
      [notifyOnReselect]="notifyOnReselect"
      [theme]="theme"
      [styles]="styles"
      [RenderContainer]="renderContainer && onRenderContainer"
      [RenderList]="renderList && onRenderList"
      [RenderItem]="renderItem && onRenderItem"
      [RenderOption]="renderOption && onRenderOption"
      [RenderPlaceholder]="renderPlaceholder && onRenderPlaceholder"
      [RenderPlaceHolder]="renderPlaceHolder && onRenderPlaceHolder"
      [RenderTitle]="renderTitle && onRenderTitle"
      [RenderCaretDown]="renderCaretDown && onRenderCaretDown"
      [Change]="onChangeHandler"
      [Dismiss]="onDismissHandler"
    ></Dropdown>
  `, isInline: true, changeDetection: i0.ChangeDetectionStrategy.OnPush });
FabDropdownComponent = __decorate([
    AngularReact(),
    Styled('FabDropdownComponent')
], FabDropdownComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDropdownComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'fab-dropdown',
                    exportAs: 'fabDropdown',
                    template: `
    <Dropdown
      #reactNode
      [componentRef]="componentRef"
      [label]="label"
      [ariaLabel]="ariaLabel"
      [id]="id"
      [className]="className"
      [defaultSelectedKey]="defaultSelectedKey"
      [selectedKey]="selectedKey"
      [disabled]="disabled"
      [required]="required"
      [calloutProps]="calloutProps"
      [panelProps]="panelProps"
      [errorMessage]="errorMessage"
      [placeholder]="placeholder"
      [openOnKeyboardFocus]="openOnKeyboardFocus"
      [options]="options"
      [dropdownWidth]="dropdownWidth"
      [responsiveMode]="responsiveMode"
      [multiSelect]="multiSelect"
      [defaultSelectedKeys]="defaultSelectedKeys"
      [selectedKeys]="selectedKeys"
      [multiSelectDelimiter]="multiSelectDelimiter"
      [notifyOnReselect]="notifyOnReselect"
      [theme]="theme"
      [styles]="styles"
      [RenderContainer]="renderContainer && onRenderContainer"
      [RenderList]="renderList && onRenderList"
      [RenderItem]="renderItem && onRenderItem"
      [RenderOption]="renderOption && onRenderOption"
      [RenderPlaceholder]="renderPlaceholder && onRenderPlaceholder"
      [RenderPlaceHolder]="renderPlaceHolder && onRenderPlaceHolder"
      [RenderTitle]="renderTitle && onRenderTitle"
      [RenderCaretDown]="renderCaretDown && onRenderCaretDown"
      [Change]="onChangeHandler"
      [Dismiss]="onDismissHandler"
    ></Dropdown>
  `,
                    changeDetection: ChangeDetectionStrategy.OnPush,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }, { type: i0.Renderer2 }]; }, propDecorators: { reactNodeRef: [{
                type: ViewChild,
                args: ['reactNode', { static: true }]
            }], dropdownOptionsDirective: [{
                type: ContentChild,
                args: [DropdownOptionsDirective, { static: true }]
            }], componentRef: [{
                type: Input
            }], label: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], id: [{
                type: Input
            }], className: [{
                type: Input
            }], defaultSelectedKey: [{
                type: Input
            }], selectedKey: [{
                type: Input
            }], disabled: [{
                type: Input
            }], required: [{
                type: Input
            }], calloutProps: [{
                type: Input
            }], panelProps: [{
                type: Input
            }], errorMessage: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], openOnKeyboardFocus: [{
                type: Input
            }], options: [{
                type: Input
            }], dropdownWidth: [{
                type: Input
            }], responsiveMode: [{
                type: Input
            }], multiSelect: [{
                type: Input
            }], defaultSelectedKeys: [{
                type: Input
            }], selectedKeys: [{
                type: Input
            }], multiSelectDelimiter: [{
                type: Input
            }], notifyOnReselect: [{
                type: Input
            }], theme: [{
                type: Input
            }], styles: [{
                type: Input
            }], renderContainer: [{
                type: Input
            }], renderList: [{
                type: Input
            }], renderItem: [{
                type: Input
            }], renderOption: [{
                type: Input
            }], renderPlaceholder: [{
                type: Input
            }], renderPlaceHolder: [{
                type: Input
            }], renderTitle: [{
                type: Input
            }], renderCaretDown: [{
                type: Input
            }], onChange: [{
                type: Output
            }], onDismiss: [{
                type: Output
            }] } });

// Copyright (c) Microsoft Corporation. All rights reserved.
const declarations = [
    FabDropdownComponent,
    DropdownOptionDirective,
    DropdownOptionsDirective
];
class FabDropdownModule {
    constructor() {
        // Add any React elements to the registry (used by the renderer).
        registerElement('Dropdown', () => Dropdown);
    }
}
/** @nocollapse */ FabDropdownModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
/** @nocollapse */ FabDropdownModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDropdownModule, declarations: [FabDropdownComponent,
        DropdownOptionDirective,
        DropdownOptionsDirective], imports: [CommonModule], exports: [FabDropdownComponent,
        DropdownOptionDirective,
        DropdownOptionsDirective] });
/** @nocollapse */ FabDropdownModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDropdownModule, imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.1", ngImport: i0, type: FabDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: declarations,
                    exports: declarations,
                    schemas: [NO_ERRORS_SCHEMA],
                }]
        }], ctorParameters: function () { return []; } });

// Copyright (c) Microsoft Corporation. All rights reserved.

/**
 * Generated bundle index. Do not edit.
 */

export { DropdownOptionDirective, DropdownOptionsDirective, FabDropdownComponent, FabDropdownModule };
